import { Camera, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar } from "@/components/avatar";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { cn } from "@/utils/utils";

/** What gets stored: a 160px square JPEG, whatever came in. */
const SIZE = 160;
/** The crop stage's viewport, in CSS pixels. */
const STAGE = 288;

/** Only offered where it can work: a secure context with a camera API. */
const cameraAvailable = () =>
  typeof navigator !== "undefined" &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  window.isSecureContext;

/**
 * A profile picture: the avatar with a camera badge, and everything else behind
 * it.
 *
 * Two buttons sat beside the picture and read as a form control rather than a
 * picture you can change. Pressing the badge opens the choice — a file, or the
 * camera — and either way the image lands in a crop stage first, so what is
 * saved is a square someone framed rather than whatever shape the file was.
 *
 * Nothing large is ever sent: the crop is drawn to a 160px canvas here, so a
 * 12-megapixel photo leaves as a few kilobytes of JPEG.
 */
export function AvatarPicker({
  src,
  name,
  onChange,
  onRemove,
  busy = false,
  className,
  editable = true,
}: {
  src: string | null | undefined;
  name: string;
  onChange: (dataUrl: string) => void | Promise<void>;
  onRemove?: (() => void) | undefined;
  busy?: boolean;
  className?: string | undefined;
  /** False shows the picture and nothing else — a record you may not change. */
  editable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const avatar = (
    <Avatar src={src} name={name} className={cn("size-24 rounded-2xl text-3xl", className)} />
  );

  if (!editable) return avatar;

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen(true)}
        className="group relative shrink-0 rounded-2xl disabled:opacity-60"
        title="Change picture"
      >
        {avatar}
        <span className="bg-accent text-accent-fg ring-surface-raised absolute -right-1.5 -bottom-1.5 grid size-8 place-items-center rounded-full ring-4 transition-transform group-hover:scale-105">
          <Camera className="size-4" aria-hidden />
        </span>
        <span className="sr-only">Change picture</span>
      </button>

      {open ? (
        <PictureDialog
          hasPicture={Boolean(src)}
          onClose={() => setOpen(false)}
          onRemove={
            onRemove
              ? () => {
                  setOpen(false);
                  onRemove();
                }
              : undefined
          }
          onDone={(dataUrl) => {
            setOpen(false);
            void onChange(dataUrl);
          }}
        />
      ) : null}
    </>
  );
}

/** Choose a source, frame it, save it — three steps in one dialog. */
function PictureDialog({
  hasPicture,
  onDone,
  onRemove,
  onClose,
}: {
  hasPicture: boolean;
  onDone: (dataUrl: string) => void;
  onRemove?: (() => void) | undefined;
  onClose: () => void;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const readFile = (chosen: File | undefined) => {
    if (!chosen) return;
    if (!chosen.type.startsWith("image/")) {
      toast.error("That file is not an image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSource(String(reader.result));
    reader.onerror = () => toast.error("That file could not be read.");
    reader.readAsDataURL(chosen);
  };

  if (source) {
    return <CropStage source={source} onBack={() => setSource(null)} onDone={onDone} />;
  }
  if (camera) {
    return <CameraStage onClose={() => setCamera(false)} onCapture={setSource} />;
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Change picture"
      description="Pick a file or use the camera. You choose the crop before it is saved."
      footer={
        <>
          {onRemove && hasPicture ? (
            <Button variant="ghost" className="mr-auto" onClick={onRemove}>
              <Trash2 aria-hidden /> Remove picture
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="grid gap-2 pb-2">
        <input
          ref={file}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(event) => {
            readFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <Button variant="secondary" onClick={() => file.current?.click()}>
          <Upload aria-hidden /> Upload a photo
        </Button>
        {cameraAvailable() ? (
          <Button variant="secondary" onClick={() => setCamera(true)}>
            <Camera aria-hidden /> Take a photo
          </Button>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Frame the picture: drag to move, slide to zoom.
 *
 * The preview and the export share one transform — a scale and an offset in
 * stage pixels — so what is saved is exactly what was framed, scaled down by
 * the ratio between the stage and the stored size. The offset is clamped so
 * the square is always covered; there is no way to frame an empty corner.
 */
function CropStage({
  source,
  onDone,
  onBack,
}: {
  source: string;
  onDone: (dataUrl: string) => void;
  onBack: () => void;
}) {
  const image = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);

  // "Cover" is the floor: at zoom 1 the shorter side exactly fills the stage.
  const base = natural ? Math.max(STAGE / natural.width, STAGE / natural.height) : 1;
  const scale = base * zoom;
  const width = (natural?.width ?? 0) * scale;
  const height = (natural?.height ?? 0) * scale;

  const clamp = (next: { x: number; y: number }) => ({
    x: Math.min(0, Math.max(STAGE - width, next.x)),
    y: Math.min(0, Math.max(STAGE - height, next.y)),
  });

  // Re-centre whenever the zoom changes, keeping the middle of the stage put.
  useEffect(() => {
    setOffset((current) => ({
      x: Math.min(0, Math.max(STAGE - width, current.x)),
      y: Math.min(0, Math.max(STAGE - height, current.y)),
    }));
  }, [width, height]);

  const onLoad = () => {
    const element = image.current;
    if (!element) return;
    const cover = Math.max(STAGE / element.naturalWidth, STAGE / element.naturalHeight);
    setNatural({ width: element.naturalWidth, height: element.naturalHeight });
    setOffset({
      x: (STAGE - element.naturalWidth * cover) / 2,
      y: (STAGE - element.naturalHeight * cover) / 2,
    });
  };

  const save = () => {
    const element = image.current;
    if (!element || !natural) return;
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    // The stage, shrunk: same framing, stored size.
    const ratio = SIZE / STAGE;
    canvas
      .getContext("2d")
      ?.drawImage(element, offset.x * ratio, offset.y * ratio, width * ratio, height * ratio);
    onDone(canvas.toDataURL("image/jpeg", 0.85));
  };

  return (
    <Modal
      open
      onClose={onBack}
      size="sm"
      title="Frame the picture"
      description="Drag to move it, and use the slider to zoom."
      footer={
        <>
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button disabled={!natural} onClick={save}>
            Save picture
          </Button>
        </>
      }
    >
      <div className="grid justify-items-center gap-4 pb-2">
        <div
          className="bg-surface-inset relative touch-none overflow-hidden rounded-2xl"
          style={{ width: STAGE, height: STAGE }}
          onPointerDown={(event) => {
            drag.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!drag.current) return;
            setOffset(
              clamp({ x: event.clientX - drag.current.x, y: event.clientY - drag.current.y }),
            );
          }}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
        >
          <img
            ref={image}
            src={source}
            alt=""
            draggable={false}
            onLoad={onLoad}
            className="max-w-none cursor-grab select-none active:cursor-grabbing"
            style={{
              width: width || undefined,
              height: height || undefined,
              transform: `translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
        </div>
        <label className="flex w-full max-w-72 items-center gap-3">
          <span className="text-fg-muted text-subhead">Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="accent-accent w-full"
          />
        </label>
      </div>
    </Modal>
  );
}

/**
 * The camera, for as long as this is on screen and not a moment longer.
 *
 * The stream is stopped in the effect's cleanup, so closing the dialog — by
 * button, by Escape, or by the component going away — turns the light off. A
 * page that leaves the camera running is a bug people notice once and never
 * trust again. The full frame is handed on; the crop stage does the framing.
 */
function CameraStage({
  onCapture,
  onClose,
}: {
  onCapture: (dataUrl: string) => void;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user", width: 1280, height: 720 } })
      .then((granted) => {
        // Opened and closed faster than the prompt was answered: stop it rather
        // than attach a stream to an element that is gone.
        if (cancelled) {
          granted.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = granted;
        if (video.current) {
          video.current.srcObject = granted;
          setReady(true);
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof DOMException && cause.name === "NotAllowedError"
            ? "The camera was not allowed. Check the site's permissions and try again."
            : "No camera could be started on this device.",
        );
      });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const take = () => {
    const element = video.current;
    if (!element) return;
    const canvas = document.createElement("canvas");
    canvas.width = element.videoWidth;
    canvas.height = element.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    // Taken as it was previewed: the mirror is undone here, not left to
    // surprise someone when the saved picture faces the other way.
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(element, 0, 0);
    onCapture(canvas.toDataURL("image/jpeg", 0.92));
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Take a photo"
      description="You will be able to crop it next."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button disabled={!ready} onClick={take}>
            <Camera aria-hidden /> Capture
          </Button>
        </>
      }
    >
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout rounded-lg px-3 py-2.5"
        >
          {error}
        </p>
      ) : (
        <div className="bg-surface-inset aspect-square overflow-hidden rounded-xl">
          <video
            ref={video}
            autoPlay
            playsInline
            muted
            // Mirrored, because a preview of your own face that moves the wrong
            // way is the one thing everybody finds unusable.
            className="size-full scale-x-[-1] object-cover"
          />
        </div>
      )}
    </Modal>
  );
}
